import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { Popover, TableCell } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DialogConfirmDeleteArtwork from 'commons/Dialog/DialogConfirmDeleteArtwork';
import Text from 'commons/Text';
import API from 'configs/API';
import Colors from 'configs/Colors';
import useDeleteArtwork from 'hooks/useDeleteArtwork';
import DialogTransferOwnerCertificate from 'pages/ManageCertificate/components/ManageCertificateTable/components/ManageCertificateTableBody/components/TableCertificateAction/DialogTransferOwnerCertificate';
import useStatusCanRequestCertificate from 'react-query/hooks/queries/useStatusCanRequestCertificate';
import Routes from 'utils/Route';
import { axiosPatch } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

import useStyles from './styles';

const TableArtworkCollectorAction = ({ artwork }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isShowDialogDelete, setIsShowDialogDelete] = useState(false);
  const [isShowTransferOwner, setIsShowTransferOwner] = useState(false);

  const { mutate: deleteArtwork } = useDeleteArtwork();
  const { data: status } = useStatusCanRequestCertificate();

  const updateArtworkOwnerMutation = useMutation({
    mutationFn: ({ artworkId, newOwnerId }) => {
      return axiosPatch(false, API.ARTWORK.ARTWORK_DETAIL(artworkId), {
        owner: newOwnerId,
        status: 'in_stock',
      });
    },
  });

  const open = Boolean(anchorEl);

  const handleOpenPopover = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleClickRequestCertificate = () => {
    navigate(Routes.RequestCertificateId(artwork?.id));
  };

  const handleNavigateEditArtwork = () => {
    navigate(Routes.EditArtwork(artwork?.id));
  };

  const handleShowPopUpConfirmDelete = () => {
    setAnchorEl(null);
    setIsShowDialogDelete(true);
  };

  const handleCancelDelete = () => {
    setIsShowDialogDelete(false);
  };

  const handleDeleteArtwork = () => {
    deleteArtwork(artwork?.id, {
      onSuccess: () => {
        ToastTopHelper.success(
          t(
            artwork?.has_certificate ? 'text_move_artwork_to_store' : 'text_delete_artwork_success',
          ),
        );
      },
    });

    setIsShowDialogDelete(false);
  };

  const handleOpenTransferOwner = () => {
    setAnchorEl(null);
    setIsShowTransferOwner(true);
  };

  const handleCloseTransferOwner = () => {
    setIsShowTransferOwner(false);
  };

  const handleSubmitTransferOwner = async (selectedUser) => {
    if (!artwork?.has_certificate || !artwork?.certificate?.code) {
      const { success } = await updateArtworkOwnerMutation.mutateAsync({
        artworkId: artwork?.id,
        newOwnerId: selectedUser?.id,
      });

      if (success) {
        ToastTopHelper.success(t('artwork_owner_updated'));
        queryClient.invalidateQueries({
          queryKey: [API.ARTWORK.LIST_ARTWORK_EXCLUDING_SOLD_DONATED_GIFTED],
        });
        handleCloseTransferOwner();
      } else {
        ToastTopHelper.error(t('update_failed'));
      }
      return;
    }
  };

  const canRequestCertificate =
    Array.isArray(status) && status?.find((item) => item?.value === artwork?.status?.value);

  const isCertificateDisabled = !canRequestCertificate || artwork?.has_certificate;

  return (
    <TableCell className={classes.tableCell}>
      <MoreHorizOutlinedIcon
        onClick={handleOpenPopover}
        className={classes.iconOutLine}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={classes.popover}
      >
        <Text
          type="sm"
          clickAble
          className={clsx(classes.textItemPopover, {
            [classes.disable]: isCertificateDisabled,
          })}
          onClick={handleClickRequestCertificate}
        >
          {t('request_certificate')}
        </Text>

        <Text
          type="sm"
          clickAble
          className={classes.textItemPopover}
          onClick={handleNavigateEditArtwork}
        >
          {t('edit_artwork')}
        </Text>

        <Text
          type="sm"
          clickAble
          className={clsx(classes.textItemPopover)}
          onClick={handleOpenTransferOwner}
        >
          {t('tranfer_owner')}
        </Text>

        <Text
          type="sm"
          clickAble
          onClick={handleShowPopUpConfirmDelete}
          className={classes.textItemPopover}
          color={Colors.Error}
        >
          {t('delete_artwork')}
        </Text>
      </Popover>

      <DialogConfirmDeleteArtwork
        isShowDialog={isShowDialogDelete}
        onCancel={handleCancelDelete}
        onSubmit={handleDeleteArtwork}
        hasCertificate={artwork?.has_certificate}
      />

      <DialogTransferOwnerCertificate
        open={isShowTransferOwner}
        onClose={handleCloseTransferOwner}
        onSubmit={handleSubmitTransferOwner}
        certificate={artwork?.certificate}
      />
    </TableCell>
  );
};

export default TableArtworkCollectorAction;
